import { isValidPhoneNumber } from "libphonenumber-js";
import { ValiError, email, nonEmpty, parse, pipe, string } from "valibot";

import { ParticipantStatus, ParticipantTypes } from "@api/graphql";
import { ValidationErrors } from "@pages/register/hooks/errorResolver";
import { UserDetailsInput } from "@pages/register/steps/RegisterFormTypes";
import { isArtistBeneficiary } from "@util/index";
import { E164_MOBILE_NUMBER_FORMAT } from "@util/regex";

export const ContactDetailsResolver = async (
  data: UserDetailsInput,
  userStatus?: string,
  participantTypes?: ParticipantTypes[],
): Promise<{ values: UserDetailsInput; errors: ValidationErrors<UserDetailsInput> }> => {
  const errors: ValidationErrors<UserDetailsInput> = {};

  const isArtistOrBeneficiaryParticipant = isArtistBeneficiary(participantTypes);
  const isMobileNumberMandatory =
    isArtistOrBeneficiaryParticipant &&
    (userStatus === ParticipantStatus.ACTIVE || userStatus === ParticipantStatus.PENDING_APPROVAL);
  const isEmailMandatory =
    isArtistOrBeneficiaryParticipant &&
    (userStatus === ParticipantStatus.ACTIVE || userStatus === ParticipantStatus.PENDING_APPROVAL);

  // First name and last name are always mandatory
  if (!data.contact_details?.first_name) {
    errors.contact_details = {
      ...errors.contact_details,
      first_name: {
        type: "required",
        message: "First name is required.",
      },
    };
  }

  if (!data.contact_details?.last_name) {
    errors.contact_details = {
      ...errors.contact_details,
      last_name: {
        type: "required",
        message: "Last name is required.",
      },
    };
  }

  const phoneNumber = data.contact_details?.phone_number?.trim();
  const errorMessage = `Oops, that number isn't in the right format. Please start with a plus sign '+' and add the country code ('61' for Australia), omit the first '0', then write the mobile number (no spaces)`;
  const validatePhoneNumber = (phoneNumber: string) => {
    return !E164_MOBILE_NUMBER_FORMAT.test(phoneNumber) || !isValidPhoneNumber(phoneNumber);
  };

  if (data.contact_details?.status !== ParticipantStatus.INACTIVE && isMobileNumberMandatory && !phoneNumber) {
    errors.contact_details = {
      ...errors.contact_details,
      phone_number: {
        type: "required",
        message: "Mobile number is required.",
      },
    };
  } else if (phoneNumber && validatePhoneNumber(phoneNumber)) {
    errors.contact_details = {
      ...errors.contact_details,
      phone_number: {
        type: "pattern",
        message: errorMessage,
      },
    };
  }

  const emailValue = data.contact_details?.email?.trim();
  if (isEmailMandatory && !emailValue) {
    errors.contact_details = {
      ...errors.contact_details,
      email: {
        type: "required",
        message: "Email address is required.",
      },
    };
  } else if (emailValue) {
    const emailSchema = pipe(string(), nonEmpty("Email is required"), email("Invalid email address format."));
    try {
      parse(emailSchema, emailValue);
    } catch (error) {
      if (error instanceof ValiError) {
        errors.contact_details = {
          ...errors.contact_details,
          email: {
            type: "pattern",
            message: error.message,
          },
        };
      } else {
        console.error("Unexpected error during validation:", { error });
      }
    }
  }

  return {
    values: data,
    errors,
  };
};
